import { lazy, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { motion, useInView, useAnimation } from 'framer-motion';
import Lenis from '@studio-freight/lenis';
import { Box, useTheme } from '@mui/material';

import Footer from '../../components/Footer';
import PoweredBySparkl from '../../components/PoweredBySparkl';
import { ThemeContext } from '../../globalContext/theme/themeProvider';
import { SubscriptionContext } from '../../globalContext/subscriptions/subscriptionsProvider';
import { useWindowDimension } from '../../hooks/useWindowDimension';
import { MUI_MOBILE_BREAKPOINT } from '../../consts/constants';
import { styles } from './HomeStyles';
import { ROUTES } from '../../route/routes';
import TestCountdownBanner from '../../components/TestCountdownBanner';
import Chat from '../../components/Chat';

const BottomNavbar = lazy(() => import('../../components/BottomNavbar'));
const ClassJoinPopup = lazy(
  () => import('../../containers/ClassJoinPopup/ClassJoinPopup'),
);
const FreeAccountDashboard = lazy(
  () => import('../../containers/FreeAccountDashboard'),
);
const Sidebar = lazy(() => import('../../components/Sidebar'));
const QuoteOfDay = lazy(() => import('../../containers/QuoteOfDay'));


const Home = () => {
  const theme = useTheme();
  const classes = styles(theme);
  const [width] = useWindowDimension();
  const scrollContainerRef = useRef<HTMLElement>(null);

  const isInView = useInView(scrollContainerRef, { once: true });
  const mainControls = useAnimation();

  useEffect(() => {
    if (isInView) {
      mainControls.start('visible');
    }
  }, [isInView]);

  const [subscriptionInfo] = useContext(SubscriptionContext);
  const [themeState] = useContext(ThemeContext);
  const isHaleBrand = themeState?.brand?.key === 'hale';
  const isMobile = width <= MUI_MOBILE_BREAKPOINT;

  const isAccountPaid = subscriptionInfo?.subscription?.paid === 1;

  useEffect(() => {
    if (!isAccountPaid) {
      if (!scrollContainerRef.current) return;

      const lenis = new Lenis({
        wrapper: scrollContainerRef.current,
        content: scrollContainerRef.current.firstElementChild as HTMLElement,
        syncTouch: true,
        duration: 1,
      });

      const raf = (time: number) => {
        lenis.raf(time);
        requestAnimationFrame(raf);
      };

      requestAnimationFrame(raf);
      return () => {
        lenis.destroy();
      };
    }
  }, []);

  const currentPath = window.location.pathname;
  const isFullPage =
    currentPath.includes(ROUTES.AI_TUTOR) ||
    currentPath.includes(ROUTES.SPARKY) ||
    currentPath.includes(ROUTES.VIDEO_PRESENTATION);

  const location = useLocation();
  const [showNotification, setShowNotification] = useState(true);

  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const handleBannerVisibilityChange = useCallback((isVisible: boolean) => {
    setIsBannerVisible(isVisible);
  }, []);

  // When navigating via sidebar, ensure the new route content is visible
  useEffect(() => {
    mainControls.start('visible');
  }, [location.pathname, mainControls]);

  useEffect(() => {
    const path = location.pathname;
    const shouldHideNotification = path.includes(ROUTES.AI_TUTOR) || path.includes(ROUTES.SPARKY) || path.includes(ROUTES.VIDEO_PRESENTATION);
    setShowNotification(!shouldHideNotification);
  }, [location.pathname]);

  return (
    <>
      <Box sx={classes.container}>
        {!isMobile && isAccountPaid && !isFullPage ? <Sidebar /> : <></>}
        {isAccountPaid  && <TestCountdownBanner
          onVisibilityChange={handleBannerVisibilityChange} />}

        <Box
          id="page-scroll"
          sx={{
            ...(!isFullPage
              ? classes.mainPage
              : { flex: 1, minWidth: 0, width: '100%', maxWidth: '100%' }),
            ...(!isAccountPaid ? classes.removeMargin : {}),
            ...(showNotification && isAccountPaid
              ? { marginTop: '3rem' }
              : {}),
            ...(isBannerVisible ? { paddingTop: '2.5rem' } : {}),
          }}
          ref={scrollContainerRef}
        >
          <Box sx={classes.mainContainer}>
            <Box sx={classes.topRow}>
              {isAccountPaid ? (
                <motion.div
                  variants={{
                    hidden: { opacity: 0, y: 75 },
                    visible: { opacity: 1, y: 0 },
                  }}
                  initial="hidden"
                  animate={mainControls}
                  transition={{ duration: 0.5, delay: 0.25 }}
                >
                  <Outlet key={location.pathname} />
                </motion.div>
              ) : (
                <FreeAccountDashboard />
              )}
            </Box>
            {!isAccountPaid ? (
              <Footer />
            ) : (
              <Box
                sx={{
                  ...classes.bottomRow,
                  ...(isHaleBrand ? classes.haleBottomRow : {}),
                }}
              >
                {!isFullPage &&
                  (isHaleBrand ? (
                    <>
                      <Box sx={classes.haleQuoteWrapper}>
                        <QuoteOfDay />
                      </Box>
                      <PoweredBySparkl />
                    </>
                  ) : (
                    <QuoteOfDay />
                  ))}
              </Box>
            )}
            {isMobile && isAccountPaid && !isFullPage && (
              <BottomNavbar scrollContainerRef={scrollContainerRef} />
            )}
          </Box>
        </Box>
      </Box>
      <ClassJoinPopup {...{ isAccountPaid }} />
      <Chat dashboard={true} />
    </>
  );
};

export default Home;
